import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './ComunicacaoMassa.css';
import {
    FaPaperPlane, FaSpinner, FaCheckCircle, FaExclamationTriangle,
    FaSms, FaEnvelope, FaPaperclip, FaSearch, FaTimes,
    FaChevronLeft, FaChevronRight, FaUsers, FaCheck,
    FaBolt, FaFilter, FaSortAmountDown,
} from 'react-icons/fa';

export default function ComunicacaoMassa() {
    const [clientes,            setClientes]            = useState([]);
    const [tagsDinamicas,       setTagsDinamicas]       = useState([]);
    const [templates,           setTemplates]           = useState([]);
    const [clientesSelecionados, setClientesSelecionados] = useState(new Set());

    const [termoBusca, setTermoBusca] = useState('');
    const [page,       setPage]       = useState(1);
    const [limit]                     = useState(50);
    const [totalClientes, setTotalClientes] = useState(0);
    const [totalPages,    setTotalPages]    = useState(1);

    const [modalAberto,  setModalAberto]  = useState(false);
    const [tipoCampanha, setTipoCampanha] = useState('sms');
    const [assunto,      setAssunto]      = useState('');
    const [mensagem,     setMensagem]     = useState('');
    const [anexo,        setAnexo]        = useState(null);

    const [loading,   setLoading]   = useState(true);
    const [enviando,  setEnviando]  = useState(false);
    const [resultado, setResultado] = useState(null);

    useEffect(() => {
        async function fetchConfig() {
            try {
                const [tagsRes, templatesRes] = await Promise.all([
                    api.get('/api/tags'),
                    api.get('/api/templates'),
                ]);
                setTagsDinamicas(tagsRes.data);
                setTemplates(templatesRes.data);
            } catch (err) {
                console.error('Erro ao carregar configurações:', err);
            }
        }
        fetchConfig();
    }, []);

    const carregarClientes = useCallback(async (paginaAtual, busca) => {
        setLoading(true);
        try {
            const res = await api.get('/api/clients/', {
                params: { page: paginaAtual, limit, query: busca },
            });
            setClientes(res.data.data);
            setTotalClientes(res.data.pagination.total);
            setTotalPages(res.data.pagination.totalPages);
        } catch (err) {
            console.error('Erro ao carregar clientes:', err);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    useEffect(() => { carregarClientes(page, termoBusca); }, [page]); // eslint-disable-line

    useEffect(() => {
        const t = setTimeout(() => { setPage(1); carregarClientes(1, termoBusca); }, 600);
        return () => clearTimeout(t);
    }, [termoBusca, carregarClientes]);

    const getIniciais = (nome) => {
        if (!nome) return 'CL';
        const p = nome.trim().split(' ');
        return p.length === 1 ? p[0].substring(0, 2).toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
    };

    const AVATAR_COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#14b8a6','#f97316'];
    const getAvatarColor = (nome) => AVATAR_COLORS[(nome?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

    const handleSelectCliente = (id) => {
        const s = new Set(clientesSelecionados);
        s.has(id) ? s.delete(id) : s.add(id);
        setClientesSelecionados(s);
    };

    const selecionarVisiveis = () => {
        const s = new Set(clientesSelecionados);
        clientes.forEach(c => s.add(c.id));
        setClientesSelecionados(s);
    };

    const limparSelecao = () => setClientesSelecionados(new Set());

    const abrirModal = (tipo) => { setTipoCampanha(tipo); setResultado(null); setModalAberto(true); };

    const fecharModal = () => {
        if (enviando) return;
        setModalAberto(false);
        setMensagem(''); setAssunto(''); setAnexo(null); setResultado(null);
    };

    const handleEnviar = async () => {
        if (!mensagem.trim()) { alert('Escreva uma mensagem.'); return; }
        if (tipoCampanha === 'email' && !assunto.trim()) { alert('Assunto é obrigatório.'); return; }
        setEnviando(true); setResultado(null);
        try {
            const ids = Array.from(clientesSelecionados);
            let response;
            if (tipoCampanha === 'sms') {
                response = await api.post('/api/clients/send-sms-mass', { clientIds: ids, mensagem });
            } else {
                const formData = new FormData();
                formData.append('tipo', 'email');
                formData.append('mensagem', mensagem);
                formData.append('assunto', assunto);
                ids.forEach(id => formData.append('clientIds[]', id));
                if (anexo) formData.append('anexo', anexo);
                response = await api.post('/api/clients/send-email-mass', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            setResultado({ sucesso: true, ...response.data });
            setTimeout(() => { fecharModal(); limparSelecao(); }, 3000);
        } catch (error) {
            setResultado({ sucesso: false, message: error.response?.data?.error || 'Erro no servidor.' });
        } finally {
            setEnviando(false);
        }
    };

    const temSelecionados = clientesSelecionados.size > 0;

    return (
        <div className="cm-root">

            {/* ── HERO HEADER ── */}
            <div className="cm-hero">
                <div className="cm-hero-left">
                    <div className="cm-hero-icon"><FaBolt /></div>
                    <div>
                        <h1 className="cm-hero-title">Comunicação em Massa</h1>
                        <p className="cm-hero-sub">Selecione destinatários e dispare campanhas de SMS ou e-mail</p>
                    </div>
                </div>
                <div className="cm-hero-stats">
                    <div className="cm-stat-pill">
                        <FaUsers />
                        <span><strong>{totalClientes.toLocaleString()}</strong> clientes</span>
                    </div>
                    {temSelecionados && (
                        <div className="cm-stat-pill accent">
                            <FaCheck />
                            <span><strong>{clientesSelecionados.size}</strong> selecionados</span>
                        </div>
                    )}
                </div>
            </div>

            {/* ── SEARCH + CONTROLS ── */}
            <div className="cm-toolbar">
                <div className="cm-searchbar">
                    <FaSearch className="cm-searchbar-icon" />
                    <input
                        type="text"
                        placeholder="Buscar por nome, telefone ou e-mail..."
                        value={termoBusca}
                        onChange={e => setTermoBusca(e.target.value)}
                    />
                    {termoBusca && (
                        <button className="cm-searchbar-clear" onClick={() => setTermoBusca('')}><FaTimes /></button>
                    )}
                </div>
                <div className="cm-toolbar-right">
                    <button className="cm-btn-ghost" onClick={selecionarVisiveis}>
                        <FaCheck /> Selecionar página
                    </button>
                    {temSelecionados && (
                        <button className="cm-btn-ghost danger" onClick={limparSelecao}>
                            <FaTimes /> Limpar seleção
                        </button>
                    )}
                    <div className="cm-pagination-compact">
                        <button disabled={page === 1} onClick={() => setPage(p => p - 1)}><FaChevronLeft /></button>
                        <span>{page} / {totalPages || 1}</span>
                        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><FaChevronRight /></button>
                    </div>
                </div>
            </div>

            {/* ── LISTA ── */}
            <div className="cm-list">
                {loading ? (
                    <div className="cm-state-empty">
                        <FaSpinner className="cm-spinner" />
                        <span>Carregando clientes...</span>
                    </div>
                ) : clientes.length === 0 ? (
                    <div className="cm-state-empty">
                        <FaUsers style={{ fontSize: 40, opacity: 0.2 }} />
                        <span>Nenhum cliente encontrado</span>
                    </div>
                ) : clientes.map((cliente, idx) => {
                    const sel = clientesSelecionados.has(cliente.id);
                    return (
                        <div
                            key={cliente.id}
                            className={`cm-row ${sel ? 'cm-row--selected' : ''}`}
                            onClick={() => handleSelectCliente(cliente.id)}
                            style={{ animationDelay: `${idx * 0.02}s` }}
                        >
                            {/* Checkbox */}
                            <div className={`cm-checkbox ${sel ? 'cm-checkbox--checked' : ''}`}>
                                {sel && <FaCheck />}
                            </div>

                            {/* Avatar */}
                            <div className="cm-avatar" style={{ background: getAvatarColor(cliente.nome) }}>
                                {getIniciais(cliente.nome)}
                            </div>

                            {/* Info */}
                            <div className="cm-row-info">
                                <span className="cm-row-name">{cliente.nome}</span>
                                <div className="cm-row-contacts">
                                    {cliente.telefone
                                        ? <span className="cm-chip cm-chip--sms"><FaSms />{cliente.telefone}</span>
                                        : <span className="cm-chip cm-chip--empty">Sem telefone</span>
                                    }
                                    {cliente.email
                                        ? <span className="cm-chip cm-chip--email"><FaEnvelope />{cliente.email}</span>
                                        : <span className="cm-chip cm-chip--empty">Sem e-mail</span>
                                    }
                                </div>
                            </div>

                            {/* Selected check */}
                            {sel && <div className="cm-row-check"><FaCheck /></div>}
                        </div>
                    );
                })}
            </div>

            {/* ── FLOATING ACTION BAR ── */}
            {temSelecionados && (
                <div className="cm-fab">
                    <div className="cm-fab-count">
                        <div className="cm-fab-count-inner">
                            <strong>{clientesSelecionados.size}</strong>
                            <span>dest.</span>
                        </div>
                    </div>
                    <div className="cm-fab-divider" />
                    <button className="cm-fab-btn cm-fab-btn--sms" onClick={() => abrirModal('sms')}>
                        <FaSms />
                        <span>Campanha SMS</span>
                    </button>
                    <button className="cm-fab-btn cm-fab-btn--email" onClick={() => abrirModal('email')}>
                        <FaEnvelope />
                        <span>Campanha E-mail</span>
                    </button>
                </div>
            )}

            {/* ── MODAL ── */}
            {modalAberto && (
                <div className="cm-overlay" onClick={e => e.target === e.currentTarget && fecharModal()}>
                    <div className="cm-modal">

                        {/* Modal Header */}
                        <div className={`cm-modal-header cm-modal-header--${tipoCampanha}`}>
                            <div className="cm-modal-header-left">
                                <div className="cm-modal-icon">
                                    {tipoCampanha === 'sms' ? <FaSms /> : <FaEnvelope />}
                                </div>
                                <div>
                                    <h2>Campanha de {tipoCampanha === 'sms' ? 'SMS' : 'E-mail'}</h2>
                                    <p>{clientesSelecionados.size} destinatário{clientesSelecionados.size !== 1 ? 's' : ''} selecionado{clientesSelecionados.size !== 1 ? 's' : ''}</p>
                                </div>
                            </div>
                            <button className="cm-modal-close" onClick={fecharModal}><FaTimes /></button>
                        </div>

                        {/* Modal Body */}
                        <div className="cm-modal-body">

                            {/* Resultado */}
                            {resultado && (
                                <div className={`cm-result ${resultado.sucesso ? 'cm-result--ok' : 'cm-result--err'}`}>
                                    {resultado.sucesso ? <FaCheckCircle /> : <FaExclamationTriangle />}
                                    <div>
                                        <strong>{resultado.message}</strong>
                                        {resultado.sucesso && resultado.enviados != null && (
                                            <p>Enviados: <strong>{resultado.enviados}</strong> · Falhas: <strong>{resultado.falhas}</strong></p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Template selector */}
                            {templates.length > 0 && (
                                <div className="cm-field">
                                    <label>Carregar modelo</label>
                                    <div className="cm-select-wrapper">
                                        <select onChange={e => setMensagem(e.target.value)} defaultValue="">
                                            <option value="" disabled>Selecione um template...</option>
                                            {templates.map(t => <option key={t.id} value={t.texto}>{t.nome}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Assunto (email only) */}
                            {tipoCampanha === 'email' && (
                                <div className="cm-field">
                                    <label>Assunto <span className="cm-required">*</span></label>
                                    <input
                                        type="text"
                                        className="cm-input"
                                        placeholder="Ex: Novidades imperdíveis para você!"
                                        value={assunto}
                                        onChange={e => setAssunto(e.target.value)}
                                    />
                                </div>
                            )}

                            {/* Mensagem */}
                            <div className="cm-field">
                                <label>Mensagem <span className="cm-required">*</span></label>
                                <textarea
                                    className="cm-input cm-textarea"
                                    placeholder={tipoCampanha === 'sms'
                                        ? 'Olá {{nome_cliente}}, temos uma novidade para você...'
                                        : 'Prezado(a) {{nome_cliente}},\n\nGostaríamos de informar...'
                                    }
                                    value={mensagem}
                                    onChange={e => setMensagem(e.target.value)}
                                />
                                <div className="cm-field-footer">
                                    <div className="cm-tags">
                                        <span className="cm-tags-label">Variáveis:</span>
                                        <code onClick={() => setMensagem(m => m + '{{nome_cliente}}')} className="cm-tag">{'{{nome_cliente}}'}</code>
                                        {tagsDinamicas.slice(0, 4).map(tag => (
                                            <code key={tag.tag_nome} onClick={() => setMensagem(m => m + `{{${tag.tag_nome}}}`)} className="cm-tag">{`{{${tag.tag_nome}}}`}</code>
                                        ))}
                                    </div>
                                    {tipoCampanha === 'sms' && (
                                        <span className={`cm-char-count ${mensagem.length > 160 ? 'cm-char-count--warn' : ''}`}>
                                            {mensagem.length} / 160
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Anexo (email only) */}
                            {tipoCampanha === 'email' && (
                                <div className="cm-field">
                                    <label>Anexo</label>
                                    <div className="cm-attachment">
                                        <label className="cm-attachment-btn">
                                            <FaPaperclip />
                                            {anexo ? anexo.name : 'Adicionar arquivo'}
                                            <input type="file" onChange={e => setAnexo(e.target.files[0])} hidden />
                                        </label>
                                        {anexo && (
                                            <button className="cm-attachment-remove" onClick={() => setAnexo(null)}>
                                                <FaTimes /> Remover
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="cm-modal-footer">
                            <button className="cm-btn-cancel" onClick={fecharModal} disabled={enviando}>
                                Cancelar
                            </button>
                            <button
                                className={`cm-btn-send cm-btn-send--${tipoCampanha}`}
                                onClick={handleEnviar}
                                disabled={enviando || !mensagem.trim()}
                            >
                                {enviando
                                    ? <><FaSpinner className="cm-spinner" /> Enviando...</>
                                    : <><FaPaperPlane /> Disparar {clientesSelecionados.size} mensagem{clientesSelecionados.size !== 1 ? 's' : ''}</>
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}